import React, { useRef, useState } from 'react';
import {
  Typography, Button, Paper, Box, useTheme, IconButton,
  Tooltip, LinearProgress,
} from '@mui/material';
import {
  CloudUpload, DeleteOutline, PictureAsPdfOutlined, Download,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { TableCard } from '../tabLayout';
import EmptyState from '../../common/EmptyState';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import { ListRow, QuietNote } from './sectionParts';
import { admissionCycleService } from '../../../services/admissions/admissionCycleService';
import { AdmissionCycle } from '../../../types/admissions';
import { toUploadUrl } from '../../../utils/uploadUrl';

interface Props {
  cycle: AdmissionCycle;
  canEdit: boolean;
  onChanged: (next: AdmissionCycle) => void;
}

/**
 * ProspectusSection — single-PDF upload shown on the public apply page as a
 * downloadable button. The file is served from
 * /uploads/admissions/prospectus; backend endpoint handles replace + remove.
 */

const MAX_MB = 10;

const ProspectusSection: React.FC<Props> = ({ cycle, canEdit, onChanged }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const current = cycle.prospectusFilename;

  const onPick = () => fileRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      enqueueSnackbar('Only PDF files are allowed', { variant: 'error' });
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      enqueueSnackbar(`File must be under ${MAX_MB} MB`, { variant: 'error' });
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      const next = await admissionCycleService.uploadProspectus(cycle._id, file);
      enqueueSnackbar(current ? 'Prospectus replaced' : 'Prospectus uploaded', { variant: 'success' });
      onChanged(next);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Upload failed', { variant: 'error' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const [confirmRemove, setConfirmRemove] = useState(false);

  const onRemove = async () => {
    setRemoving(true);
    try {
      const next = await admissionCycleService.removeProspectus(cycle._id);
      enqueueSnackbar('Prospectus removed', { variant: 'success' });
      onChanged(next);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to remove', { variant: 'error' });
    } finally {
      setRemoving(false);
      setConfirmRemove(false);
    }
  };

  // Same frame as SectionCard; this section has no edit mode (upload and
  // remove act immediately), so it renders its own header.
  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: 2.5, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}
    >
      <Box sx={{ px: 3, pt: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>Prospectus</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          A PDF parents can download from the application page. Uploading a new one replaces it.
        </Typography>
      </Box>

      <Box sx={{ px: 3, pt: 2, pb: 3 }}>
        {uploading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

        {current ? (
          <TableCard>
            <ListRow>
              <PictureAsPdfOutlined sx={{ color: 'text.secondary' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap>{current}</Typography>
                <Typography variant="caption" color="text.secondary">Live on the application page</Typography>
              </Box>
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  component="a"
                  href={toUploadUrl('admissions/prospectus', current)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Download fontSize="small" />
                </IconButton>
              </Tooltip>
              {canEdit && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CloudUpload sx={{ fontSize: '16px !important' }} />}
                    onClick={onPick}
                    disabled={uploading}
                    sx={{ textTransform: 'none', borderColor: 'divider', color: 'text.secondary' }}
                  >
                    Replace
                  </Button>
                  <Tooltip title="Remove">
                    <span>
                      <IconButton size="small" color="error" onClick={() => setConfirmRemove(true)} disabled={removing}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </ListRow>
          </TableCard>
        ) : canEdit ? (
          <EmptyState
            dense
            icon={<PictureAsPdfOutlined />}
            title="No prospectus yet"
            description="Parents won't see a prospectus download on the application page until you upload one."
            action={{ label: 'Upload PDF', icon: <CloudUpload />, onClick: onPick }}
          />
        ) : (
          <QuietNote>No prospectus — parents won't see a download on the application page.</QuietNote>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          onChange={onFile}
          style={{ display: 'none' }}
        />

        {canEdit && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            PDF only, up to {MAX_MB} MB.
          </Typography>
        )}
      </Box>
      <ConfirmationDialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={onRemove}
        title="Remove the prospectus?"
        message="Parents can no longer download it from the application page. You can upload a new one any time."
        confirmText="Remove"
        loading={removing}
      />
    </Paper>
  );
};

export default ProspectusSection;
